import { mandingaDef } from "@eldrun/content";
import type { ClockReading } from "../../game/world-clock.ts";
import { h } from "../kit/dom.ts";

/** Por quanto tempo o letreiro do amanhecer ou do anoitecer fica na tela (ms). */
export const BANNER_MS = 5000;

/** Texto do letreiro quando a fase vira (ou `undefined` se não virou); ao amanhecer, com a Mandinga do Dia. */
export function phaseBanner(prev: { day: number; night: boolean } | undefined, now: { day: number; night: boolean }, mandinga = ""): string | undefined {
  if (!prev || (prev.day === now.day && prev.night === now.night)) return undefined;
  if (now.night) return "A noite chegou";
  const m = mandingaDef(mandinga);
  return m ? `Dia ${now.day} · ${m.name}: ${m.hint}` : `Dia ${now.day}`;
}

/**
 * Relógio do ciclo no topo (doc 05, HUD): arco com o sol ou a lua andando pela fase, número do
 * dia e o aviso da Boca da Noite nos últimos 30 s do dia. Letreiro grande ao amanhecer ("Dia N")
 * e ao anoitecer. Só mexe no DOM quando algo visível muda.
 */
export class ClockHud {
  private readonly root: HTMLElement;
  private readonly marker: HTMLElement;
  private readonly label: HTMLElement;
  private readonly banner: HTMLElement;
  private last: { day: number; night: boolean } | undefined;
  private shown = "";
  private bannerUntil = 0;

  constructor(parent: HTMLElement) {
    this.marker = h("div", { class: "astro" });
    this.label = h("span", { class: "dia" });
    this.root = h("div", { id: "relogio", role: "timer", "aria-label": "Relógio do dia" }, h("div", { class: "arco" }, this.marker), this.label);
    this.banner = h("div", { id: "letreiro", role: "status" });
    parent.append(this.root, this.banner);
  }

  update(r: ClockReading, nowMs: number, mandinga = ""): void {
    if (r.day < 0) return;
    const text = phaseBanner(this.last, r, mandinga);
    // O primeiro quadro só registra a fase (sem letreiro ao entrar na partida).
    if (text) {
      this.banner.textContent = text;
      this.banner.className = r.night ? "noite visivel" : "visivel";
      this.bannerUntil = nowMs + BANNER_MS;
    } else if (this.bannerUntil && nowMs >= this.bannerUntil) {
      this.banner.className = "";
      this.bannerUntil = 0;
    }
    this.last = { day: r.day, night: r.night };
    // O astro anda pelo arco: esquerda ao começar a fase, direita ao terminar.
    const angle = Math.PI * (1 - r.progress);
    this.marker.style.transform = `translate(${(Math.cos(angle) * 34).toFixed(1)}px, ${(-Math.sin(angle) * 26).toFixed(1)}px)`;
    const state = `${r.day}|${r.night}|${r.dusk}|${mandinga}`;
    if (state === this.shown) return;
    this.shown = state;
    this.root.classList.toggle("noite", r.night);
    this.root.classList.toggle("boca", r.dusk);
    const base = r.dusk ? `Dia ${r.day} · a noite vem aí` : r.night ? `Noite ${r.day}` : `Dia ${r.day}`;
    // A Mandinga fica no HUD o dia e a noite inteiros (doc 00).
    const m = mandingaDef(mandinga);
    this.label.textContent = m ? `${base} · ${m.name}` : base;
  }

  remove(): void {
    this.root.remove();
    this.banner.remove();
  }
}
